import './css/subtitles_override.css';

/**
 * Load subtitle from a given URL. The function can also check for existing subtitles before adding them.
 *
 * @param {Object} player - The video player object.
 * @param {string} lang - The language code for the subtitle.
 * @param {string} label - The label for the subtitle.
 * @param {string} url - The URL where the subtitle file is located.
 * @param {boolean} enabled - A boolean indicating if the subtitle should be enabled by default.
 * @param {boolean} [checkExisting=false] - An optional parameter indicating if the function should check for existing subtitles before adding.
 *
 * @returns {void}
 */
export function load_subtitle(player, lang, label, url, enabled, checkExisting = false) {
  // Check if the arguments are valid
  if (typeof player !== 'object' || typeof player.textTracks !== 'function' || typeof player.addRemoteTextTrack !== 'function') {
    const errorMessage = `[${load_subtitle.name}]: Invalid player object`;
    IVP.log(errorMessage, 'error');
    return;
  }
  if (typeof lang !== 'string' || typeof label !== 'string' || typeof url !== 'string' || typeof enabled !== 'boolean') {
    const errorMessage = `[${load_subtitle.name}]: Invalid arguments`;
    IVP.log(errorMessage, 'error');
    return;
  }

  // If we want to check for existing subtitles
  if (checkExisting) {
    IVP.log(`[${load_subtitle.name}]: Checking existing subtitles...`, 'info');

    // Get all text tracks
    let tracks = player.textTracks();

    // Check if the subtitle already exists
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].label === label && tracks[i].language === lang) {
        IVP.log(`[load_subtitle]: Subtitle already exists, not adding subtitle.`, 'info');
        return; // Exit the function without adding the subtitle
      }
    }
  }

  IVP.log(`[${load_subtitle.name}]: Loading subtitle`, 'info');

  const remoteTextTrack = player.addRemoteTextTrack({
    label: label,
    srclang: lang,
    kind: 'subtitles',
    src: url,
    mode: enabled ? 'showing' : 'hidden',
    default: enabled,
  });

  remoteTextTrack.addEventListener('load', function () {
    // the object has a reference to the TextTrack object via the `track` property
    IVP.log(`[${load_subtitle.name}]: Subtitle loaded successfully: ${lang}`, 'ok');
    console.log(remoteTextTrack);
  });
}

/**
 * Toggles the display state of subtitle tracks for a given player.
 *
 * @param {Object} player - The video player object.
 * @param {boolean} [state] - The desired display state of the subtitles. If true, the subtitles are shown. If false, the subtitles are hidden. If not provided, the function will toggle the current state.
 *
 * @returns {void}
 */
export function toggle_subtitle(player, state) {
  // Check if the player is valid and has a textTracks method
  if (typeof player !== 'object' || typeof player.textTracks !== 'function') {
    const errorMessage = `[${toggle_subtitle.name}]: Invalid player object`;
    IVP.log(errorMessage, 'error');
    return;
  }

  // Get all text tracks for the current player.
  const tracks = player.textTracks();

  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i];

    // Find the subtitles track and toggle 'showing' based on the 'state' parameter
    if (track.kind === 'subtitles') {
      if (typeof state === 'boolean') {
        track.mode = state ? 'showing' : 'hidden';
        IVP.log(`[${toggle_subtitle.name}]: Toggled subtitle: ${track.language}, ${track.mode}`, 'ok');
      } else {
        // If no 'state' is passed, toggle the state of the subtitle
        track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
        IVP.log(`[${toggle_subtitle.name}]: Toggled subtitle: ${track.language}, ${track.mode}`, 'ok');
      }
    }
  }
}

/**
 * Toggles the visibility of all subtitles for all video players.
 *
 * @returns {void}
 */
export function toggle_all_subtitles() {
  // Check if videojs.players is valid
  if (typeof videojs.players !== 'object') {
    const errorMessage = 'videojs.players is not an object';
    IVP.log(errorMessage, 'error');
    return;
  }

  // Get all text tracks for all the players
  for (const [key, player] of Object.entries(videojs.players)) {
    // Check if the player has a textTracks method
    if (typeof player.textTracks !== 'function') {
      const errorMessage = `[${toggle_all_subtitles.name}]: Player ${key} does not have a textTracks method`;
      IVP.log(errorMessage, 'error');
      continue; // Skip this player and continue with the next one
    }

    const tracks = player.textTracks();

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];

      // Find the subtitles track
      if (track.kind === 'subtitles') {
        track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
      }
    }
  }
}

/**
 * Removes subtitle tracks from a given player. If language codes are specified, only the subtitles of those languages are removed.
 *
 * @param {Object} player - The video player object.
 * @param {Array} [langs] - An array of language codes of the subtitles to be removed. If not provided or empty, all subtitle tracks are removed.
 *
 * @returns {void}
 */
export function remove_subtitles(player, langs = []) {
  // Check if the player is valid and has the necessary methods
  if (typeof player !== 'object' || typeof player.textTracks !== 'function' || typeof player.removeRemoteTextTrack !== 'function') {
    const errorMessage = `[${remove_subtitles.name}]: Invalid player object`;
    IVP.log(errorMessage, 'error');
    return;
  }

  // Get all text tracks for the current player
  const tracks = player.textTracks();

  // Iterate over all tracks
  for (let i = tracks.length - 1; i >= 0; i--) {
    const track = tracks[i];

    // Check if the track is a subtitle and (if languages are specified) if its language is in the specified list
    if (track.kind === 'subtitles' && (langs.length === 0 || langs.includes(track.language))) {
      player.removeRemoteTextTrack(track);
      IVP.log(`[${remove_subtitles.name}]: Removed subtitle: ${track.language}`, 'ok');
    }
  }
}
